import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  TemplateRef,
  computed,
  input,
  output,
  viewChild,
  viewChildren,
} from '@angular/core';
import { Menu, MenuContent, MenuItem } from '@angular/aria/menu';
import { OverlayModule } from '@angular/cdk/overlay';
import { MatIconModule } from '@angular/material/icon';

import { BaseComponent } from '../../base/base.component';
import { MenuAction, hasSubmenu } from '../menu-action.types';

/**
 * Recursive menu panel: renders one ngMenu level from a MenuAction[] config.
 *
 * Actions with `children` render as submenu triggers; the nested panel is
 * instantiated inside its own overlay and its ngMenu instance is resolved via
 * viewChildren so it can be bound to the parent item's [submenu] input
 * (template refs inside an ng-template are not visible outside of it).
 *
 * This component is internal to immo-menu — use <immo-menu> in feature code.
 */
@Component({
  selector: 'immo-menu-panel',
  templateUrl: './menu-panel.component.html',
  styleUrls: ['../theme.css', './menu-panel.component.css'],
  imports: [
    Menu,
    MenuContent,
    MenuItem,
    OverlayModule,
    MatIconModule,
    NgTemplateOutlet,
    MenuPanelComponent,
  ],
})
export class MenuPanelComponent<T = unknown> extends BaseComponent {
  // -----------------------------------------------------------------------------------------------------
  // @ INSTANCE VARIABLES
  // -----------------------------------------------------------------------------------------------------

  readonly hasSubmenu = hasSubmenu;

  // -----------------------------------------------------------------------------------------------------
  // @ INPUT VARIABLES
  // -----------------------------------------------------------------------------------------------------

  readonly actions = input.required<MenuAction<T>[]>();

  /** Optional header template rendered above the items (root level only). */
  readonly headerTemplate = input<TemplateRef<unknown> | undefined>(undefined);

  // -----------------------------------------------------------------------------------------------------
  // @ OUTPUT VARIABLES
  // -----------------------------------------------------------------------------------------------------

  /** Emits the value of the activated item; nested panels bubble through this output. */
  readonly actionSelected = output<T>();

  // -----------------------------------------------------------------------------------------------------
  // @ VIEW CHILDREN
  // -----------------------------------------------------------------------------------------------------

  /** The ngMenu instance of this panel, exposed so parents can bind it to [submenu] or [menu]. */
  readonly menuRef = viewChild.required<Menu<T>>(Menu);

  /** Direct child panels, in template order (view queries do not pierce child component templates). */
  readonly submenuPanels = viewChildren<MenuPanelComponent<T>>(MenuPanelComponent);

  // -----------------------------------------------------------------------------------------------------
  // @ COMPUTED VARIABLES
  // -----------------------------------------------------------------------------------------------------

  /** The subset of actions that open a submenu, in render order — used to index submenuPanels. */
  readonly submenuActions = computed<MenuAction<T>[]>(() =>
    this.actions().filter((action) => hasSubmenu(action)),
  );

  // -----------------------------------------------------------------------------------------------------
  // @ PUBLIC METHODS
  // -----------------------------------------------------------------------------------------------------

  /**
   * Resolves the ngMenu instance of the child panel belonging to the given action.
   *
   * Panels are created in the same order as their actions, so the i-th action with
   * children maps to the i-th direct child panel. Returns undefined while the
   * submenu overlay has not been instantiated yet — the [submenu] binding updates
   * reactively once it is.
   *
   * @param action - The submenu-bearing action to resolve the panel for.
   * @returns {Menu<T> | undefined} The ngMenu instance of the matching child panel.
   */
  getSubmenu(action: MenuAction<T>): Menu<T> | undefined {
    const index = this.submenuActions().indexOf(action);

    if (index < 0) {
      return undefined;
    }

    return this.submenuPanels()[index]?.menuRef();
  }

  /**
   * Forwards a selected action value to the parent.
   *
   * @param value - The value of the activated item.
   * @returns {void}
   */
  onItemSelected(value: T): void {
    if (this.IsNullOrUndefined(value)) {
      return;
    }

    this.actionSelected.emit(value);
  }
}
